/* eslint-disable no-underscore-dangle */
import _ from 'underscore';
import moment from 'moment';

import commonHelper, { isBlank } from '../helpers/common';

import Base, { baseInit } from './base';
import { LineItem } from './line-item';
import { Location } from './campaign';
import { ProductCategoryAllocation } from './product-category-allocation';
import { Note } from './note';

const MinPlanBudget = 0;

class Plan extends Base {
  static validations = {
    // Validation methods should return an instance of Error if invalid, null otherwise.
    // All methods must accept a val to validate as the first arg and, optionally, a reference
    // to the object as the second arg.
    advertiser(val) {
      if (_.isUndefined(val) || _.isNull(val)) {
        return new Error('Advertiser is required.');
      }
      return null;
    },

    accountExecutive(val) {
      if (_.isUndefined(val) || _.isNull(val)) {
        return new Error('Account Executive is required.');
      }
      return null;
    },

    flightStartDate(val, plan) {
      if (!plan.flightStartDate) {
        return new Error('Start date missing');
      }
      if (plan.flightEndDate) {
        const start = moment.utc(val);
        const end = moment.utc(plan.flightEndDate);
        if (start.isAfter(end)) {
          return new Error('Start date cannot be after the end date');
        }
      }
      return null;
    },

    flightEndDate(val, plan) {
      if (!plan.flightEndDate) {
        return new Error('End date missing');
      }
      if (plan.flightStartDate) {
        const end = moment.utc(val);
        const start = moment.utc(plan.flightStartDate);
        if (end.isBefore(start)) {
          return new Error('End date cannot be before the start date');
        }
      }
      return null;
    },

    budget(val, lineItemTotalBudget) {
      if (commonHelper.isNil(val)) {
        return new Error('Budget is required');
      }
      if (val <= MinPlanBudget) {
        return new Error(`Budget must be greater than $${MinPlanBudget}`);
      }
      if (lineItemTotalBudget > 0) {
        if (lineItemTotalBudget > val) {
          return new Error('Line Item total budget exceeds IO budget');
        }
      }
      return null;
    },
    campaignName(val) {
      if (_.isUndefined(val) || _.isNull(val)) {
        return new Error('Name is required.');
      }
      return null;
    },
  };

  _uuid = null; // String - internal field used for referencing unsaved plan
  // TODO: make internal with leading _ (like _uuid)
  show = false;
  _loadingLineItems = false;
  updatedPlan = false;
  _statusChanged = false; // Flag used for specifically tracking when status has changed
  _selected = false; // Boolean - checkbox selected for bulk edit.
  _new = false; // Boolean - indicates that the plan was [recently] added

  id = null; // Integer
  ownerId = null; // Integer
  accountId = null;
  advertiserId = null;
  agencyId = null;
  contactUserId = null;
  customUserId = null;
  agencyName = '';
  budget = 0; // Integer
  campaignName = null;
  files = []; // [ File{} ]
  flightStartDate = null; // Date: '2019-04-09T18:37:40.639Z';
  flightEndDate = null; // Date: '2019-04-09T18:37:40.639Z';
  impressions = 0; // Integer
  industryId = null; // Integer
  location = null; // Location
  marketingObjectiveId = null; // Integer
  productCategoryAllocations = null; // [ProductCategoryAllocation {}]
  note = null; // Note{}
  internalNote = null; // Note{}
  statusId = null; // Integer status id
  flagId = null; // Integer flag color id
  state = null;
  crPlans = []; // change request Plans, As plan Should have only one CR at a time. so it may have only one item.
  advertiserIsNew = false;
  flag = false; // Boolean flag
  flip = false; // Boolean
  spend = 0;
  pace = 0;
  lucid = false; // Boolean
  brandSafetyLevelId = 0; // Integer
  verificationPartners = null;
  ioId = null;
  source = null;

  constructor(data) {
    super(data);
    baseInit(this, data, true);

    if (this.location) {
      this.location = new Location(this.location);
    }
    if (this.lineItems) {
      this.lineItems = this.lineItems.map((li) => new LineItem(li));
    } else {
      this.lineItems = [];
    }
    if (this.flightStartDate) {
      this.flightStartDate = moment.utc(this.flightStartDate).toDate();
    }
    if (this.flightEndDate) {
      this.flightEndDate = moment.utc(this.flightEndDate).toDate();
    }
    if (this.productTypeAllocations === null) {
      this.productTypeAllocations = new ProductCategoryAllocation();
    }
    if (this.note !== null) {
      this.note = new Note(this.note);
    }
    if (this.internalNote !== null) {
      this.internalNote = new Note(this.internalNote);
    }
    if (this.crPlans) {
      this.crPlans = this.crPlans.map((cr) => new Plan(cr));
    }
    if (this.spend === null) {
      this.spend = 0;
    }
    if (this.pace === null) {
      this.pace = 0;
    }
  }

  newUUID(resetIDs) {
    super.newUUID(resetIDs);
    if (resetIDs) {
      // recurse to nested objects
      if (!isBlank(this.files)) {
        this.files.forEach((file) => {
          file.newUUID(resetIDs);
        });
      }
      if (this.note && this.note.newUUID) {
        this.note.newUUID(resetIDs);
      }
    }
  }

  /**
   * Search for and return other LineItems which share the same AdReady ID as the given Line Item
   *
   * @param {LineItem} lineItem for which to find related flights
   * @returns {LineItem}
   */
  getFlights(lineItem) {
    if (isBlank(lineItem.adreadyId)) {
      return [];
    }
    return this.lineItems.filter((li) => {
      if (lineItem.id) {
        return li.adreadyId === lineItem.adreadyId && li.id !== lineItem.id;
      }
      return li.adreadyId === lineItem.adreadyId && li._uuid !== lineItem._uuid;
    });
  }

  isDirty() {
    if (this.updatedPlan) {
      return true;
    }
    if (this.lineItems) {
      return this.lineItems.some((lineItem) => lineItem.isDirty(lineItem));
    }
    return false;
  }
}

export { ProductCategoryAllocation, Plan };
